import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';

import { Subscription } from 'rxjs';

import { OrgListSummary } from '../../../models/organizations.model';

@Component({
	selector: 'app-org-list-modal',
	styleUrls: ['./org-list-modal.component.scss'],
	templateUrl: './org-list-modal.component.html',
})
export class OrgListModalComponent implements OnInit, OnDestroy {
	filteredOrganizations: OrgListSummary[] = [];
	resultIds: string[] = [];
	searchControl = new FormControl('', { nonNullable: true });

	private readonly searchLocale = 'pt-BR';
	private searchSubscription?: Subscription;

	constructor(
		public dialogRef: MatDialogRef<OrgListModalComponent>,
		@Inject(MAT_DIALOG_DATA)
		public data: {
			disabled: string;
			organizations: OrgListSummary[];
			selected: string[];
		},
	) {}

	get hasActiveSearch(): boolean {
		return this.normalizeSearchTerm(this.searchControl.value).length > 0;
	}

	closeModal(): void {
		this.dialogRef.close();
	}

	filterOrganizations(): void {
		const organizations = Array.isArray(this.data?.organizations)
			? this.data.organizations
			: [];
		const term = this.normalizeSearchTerm(this.searchControl.value);

		this.filteredOrganizations = term
			? organizations.filter((organization) =>
					this.matchesOrganizationGroup(organization, term),
				)
			: organizations;
	}

	ngOnDestroy(): void {
		this.searchSubscription?.unsubscribe();
	}

	ngOnInit(): void {
		this.resultIds = this.data.selected;
		this.filterOrganizations();
		this.searchSubscription = this.searchControl.valueChanges.subscribe(() => {
			this.filterOrganizations();
		});
	}

	onCheckboxToggle(id: string, checked: boolean): void {
		if (!checked) {
			this.resultIds.splice(
				this.resultIds.findIndex((i) => id === i),
				1,
			);
		} else {
			this.resultIds.push(id);
		}
	}

	onClearSearch(): void {
		this.searchControl.setValue('');
	}

	saveItems(): void {
		const result = [];

		if (Array.isArray(this.data.organizations)) {
			for (const org of this.data.organizations) {
				if (this.resultIds.includes(org.id)) {
					result.push(org);
				}

				for (const orgS of org.secondary) {
					if (this.resultIds.includes(orgS.id)) {
						result.push(orgS);
					}
				}
			}
			this.dialogRef.close(result);
		}
	}

	// eslint-disable-next-line class-methods-use-this
	trackByOrganizationId(index: number, organization: { id: string }): string {
		return organization.id;
	}

	private matchesOrganizationGroup(
		organization: OrgListSummary,
		term: string,
	): boolean {
		if (this.normalizeSearchTerm(organization.name).includes(term)) {
			return true;
		}

		return (organization.secondary ?? []).some((subsidiary) =>
			this.normalizeSearchTerm(subsidiary.name).includes(term),
		);
	}

	private normalizeSearchTerm(value: string | null | undefined): string {
		return (value ?? '')
			.toLocaleLowerCase(this.searchLocale)
			.normalize('NFD')
			.replace(/[\u0300-\u036f]/g, '')
			.trim();
	}
}
